import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** After logging in, ask the engineer whether to pick up the jobs whose timers
 *  stopped by themselves at the end of the previous working day. */
export const ResumeTimerPrompt = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!user || !can("processing", "edit")) return;
    const key = `mv_resume_${user.id}_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    api.get("/processing/pending-resume")
      .then((r) => { if (r.data.length) { setJobs(r.data); sessionStorage.setItem(key, "1"); } })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  const resume = async (job) => {
    setBusy(job.id);
    try {
      await api.post(`/processing/${job.id}/timer`, { action: "start" });
      toast.success(`Timer running again for ${job.number}`);
      setJobs((j) => j.filter((x) => x.id !== job.id));
      navigate("/app/processing");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!jobs.length) return null;
  return (
    <Dialog open onOpenChange={() => setJobs([])}>
      <DialogContent className="max-w-lg" data-testid="resume-timer-prompt">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Carry on where you left off?</DialogTitle>
          <DialogDescription>
            These timers stopped by themselves at the end of the working day. Start the one you are
            picking up now - nothing is counted while a timer is off.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {jobs.map((j) => (
            <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{j.project_name}</p>
                <p className="num text-xs text-muted-foreground">
                  {j.number} &middot; {j.customer_name} &middot; {j.active_hours} h so far
                </p>
              </div>
              <Button size="sm" disabled={busy === j.id} data-testid={`resume-${j.number}`} onClick={() => resume(j)}>
                {busy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Resume
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="resume-later" onClick={() => setJobs([])}>
            <Clock className="h-4 w-4" /> Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
